import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { FieldPath } from 'react-hook-form';
import {
  Modal,
  Form,
  FormField,
  Button,
  MenuBoxIcon,
  useZodForm,
  WizardStepper,
  FORM_FIELD_MESSAGE,
  type FieldOption,
} from '@noor/ui';
import { getApiErrorMessage, getApiFieldErrors } from '@noor/api';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { menuKeys, useBranchProducts, type MenuProduct } from '@/entities/menu';
import { usePromotionDetail } from '@/entities/promotion';
import {
  buildPromotionFormSchema,
  selectedBranchIds,
  PROMOTION_NAME_MAX_LENGTH,
  type PromotionFormValues,
} from '../model/schema';
import { useUpsertPromotion } from '../model/use-upsert-promotion';
import {
  buildDefaults,
  formatDateLabel,
  formatDuration,
  mergeBranchSelection,
  parseIsoDate,
  fixedPriceCeiling,
  toUpsertVars,
} from '../lib/promotion-form';
import { BranchProductStep } from './branch-product-step';

/**
 * White bordered field pill — overrides the design system's filled default.
 * Every step-3 control (discount type, discount value, BOTH dates) shares it,
 * so the promo window reads as one row of inputs rather than two designs.
 */
const FIELD =
  'rounded-3xl border-input bg-card shadow-xs focus-visible:border-input-border-focus';

/**
 * Validation text, taken OUT OF FLOW into the space {@link GROUP} reserves.
 *
 * A `FormField` is a grid, so an in-flow message is a grid ROW: an errored
 * field grew taller than its clean siblings, which shifted every field below it
 * and left side-by-side controls unequal. Absolute positioning means an error
 * can never change a field's height.
 *
 * Anchored to `bottom-0`, INSIDE the field's own padded box — not `top-full`,
 * which starts after the padding and hung the message a full line BELOW the
 * field. Inside a modal whose body is `overflow-y-auto` that overhang is
 * clipped, which is how the date fields came to turn red while showing no
 * message at all: the text was in the DOM and announced to screen readers, and
 * simply painted outside the scroll region.
 *
 * `pointer-events-none` so the text cannot swallow clicks meant for whatever
 * sits below it.
 */
const MESSAGE = `pointer-events-none absolute bottom-0 left-0 ${FORM_FIELD_MESSAGE}`;

/** Figma label: 14px medium, -0.28px tracking, 4px inset. */
const LABEL = 'flex items-center gap-px px-1 text-sm font-medium tracking-tight';

/**
 * Figma spacing: 6px between a field's label and its control.
 *
 * `relative pb-5` anchors {@link MESSAGE} and reserves its line on every field,
 * errored or not, so validation never moves the form.
 */
const GROUP = 'relative gap-1.5 pb-6';

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', tag: '(Ru)' },
  { suffix: 'Uz', tag: '(Uz)' },
  { suffix: 'En', tag: '(En)' },
] as const;

/**
 * Which fields each step owns, so «Продолжить» validates just that step.
 *
 * Step 2's control is the `selections` map, whose cross-field rule ("at least
 * one branch carries a product") is a schema-level `.refine` reported on that
 * path — so triggering the field is what surfaces it.
 */
const STEP_FIELDS: FieldPath<PromotionFormValues>[][] = [
  ['nameRu', 'nameUz', 'nameEn'],
  ['selections'],
  ['discountType', 'discountValue', 'startDate', 'endDate'],
];

type PromotionModalDesktopProps =
  | ModalRenderProps<'add-promotion'>
  | ModalRenderProps<'edit-promotion'>;

/**
 * Create / edit promotion on desktop — a THREE-STEP wizard (Figma 1112-19909
 * name → 840-27485 branch/products → 1112-10784 discount).
 *
 * The redesign is driven by the wire, not just the visuals. Step 2 pairs the
 * branch list with that branch's product checkboxes because
 * `POST /promotions/batch` now takes `selections` — one entry per branch,
 * naming the products the promotion covers THERE. The old form's two
 * independent multi-selects could only express a cross product, so "this
 * product, only at that branch" meant creating the campaign once per branch.
 *
 * Step 1's name is likewise new: the campaign used to be named after whichever
 * product happened to be first in the batch, which meant a five-product promo
 * carried a misleading name that changed if that product was renamed.
 */
export function PromotionModalDesktop({ payload, close }: PromotionModalDesktopProps) {
  const { t } = useTranslation('eats');
  const upsert = useUpsertPromotion();
  const [step, setStep] = useState(0);

  const isEditing = payload.mode === 'edit';
  /** The branch an EDIT was opened against; `undefined` for a create. */
  const editedBranchId =
    payload.mode === 'edit' ? payload.promotion.restaurantId : undefined;

  /**
   * Which branch's products the right-hand pane shows.
   *
   * On an EDIT this starts at the promotion's own branch, so the products it
   * already covers are the first thing on screen; on a create it starts at the
   * first branch, since a wizard step that opens with an empty pane and no
   * indication of what to click reads as broken.
   */
  const [activeBranchId, setActiveBranchId] = useState<string | undefined>(() =>
    payload.mode === 'edit' ? payload.promotion.restaurantId : payload.branches[0]?.id,
  );

  const discountTypeOptions = useMemo<FieldOption[]>(
    () => [
      { label: t('promotions.form.discountType.percent'), value: 'percent' },
      { label: t('promotions.form.discountType.fixed'), value: 'fixed' },
    ],
    [t],
  );

  /**
   * The promotion's stored record AT THE BRANCH CURRENTLY OPEN.
   *
   * `GET /promotions/{id}/by-merchant` requires a `restaurant_id` and answers
   * with the products covered at that branch alone, so a campaign spanning
   * several branches is read a branch at a time — as the merchant opens each
   * one, rather than N requests when the modal opens.
   */
  const {
    data: detail,
    dataUpdatedAt,
    isPending: detailPending,
  } = usePromotionDetail(
    payload.mode === 'edit' ? String(payload.promotion.id) : undefined,
    isEditing ? activeBranchId : undefined,
  );

  /**
   * The products STOCKED AT the branch currently open — not the merchant's
   * whole catalogue.
   *
   * A product belongs to the merchant, but whether a branch sells it is a
   * per-branch `RestaurantProduct` overlay. Offering the merchant-wide list for
   * every branch let a merchant tick a product the branch does not stock, and
   * the batch create then failed the WHOLE request:
   * `4107 invalid promotion input: product is not stocked at this restaurant`.
   * Listing only what the branch actually sells makes that unpickable rather
   * than rejected after the fact.
   */
  const { data: branchProducts, isPending: branchProductsPending } =
    useBranchProducts(activeBranchId);

  /**
   * Every branch's stocked list, read straight from the query CACHE at submit
   * time rather than accumulated into state.
   *
   * The submit needs it: a product picked at branch A resolves against A's
   * list, and its PRICE is A's overlay price — `payload.products` carries the
   * merchant-wide catalogue, whose price is the base one.
   *
   * Reading the cache is what makes this free of extra state entirely. Every
   * branch the merchant opened has already been fetched under
   * `menuKeys.branchProducts(id)`, so the data is sitting there; accumulating a
   * parallel copy would only add a render pass and a way for the two to
   * disagree. Branches never opened are simply absent, and `toUpsertVars` falls
   * back to the merchant catalogue for those.
   */
  const queryClient = useQueryClient();
  function collectProductsByBranch(): Record<string, MenuProduct[]> {
    const entries = payload.branches.flatMap((branch) => {
      const cached = queryClient.getQueryData<MenuProduct[]>(
        menuKeys.branchProducts(branch.id),
      );
      return cached ? [[branch.id, cached] as const] : [];
    });
    return Object.fromEntries(entries);
  }

  const schema = useMemo(() => buildPromotionFormSchema(t), [t]);
  const form = useZodForm(schema, {
    // The row seeds the form immediately; the by-id read sharpens it when it
    // lands, so the fields are never blank while the request is in flight.
    defaultValues: buildDefaults(payload, detail),
    // `onSubmit`, not the shared `onTouched` default — this is a wizard, so
    // tabbing through step 1's three name fields must not paint required
    // errors under the ones not reached yet. «Продолжить» validates the step.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Re-seed when the read for the promotion's OWN branch lands. Keyed on that
  // branch alone: a later read for a different branch must not reset the whole
  // form (it would discard the merchant's edits to the discount and window) —
  // it only folds its product list in, which the effect below does.
  //
  // Gated on which branch was REQUESTED (`activeBranchId`), never on what the
  // response echoes back. `restaurant_id` comes back as `''` on this endpoint —
  // protobuf-JSON omits zero values, and the field is not part of the record's
  // own identity — so comparing `detail.restaurantId` to the row's branch was
  // never equal and the re-seed NEVER fired. The form then kept the row-only
  // defaults built before the read resolved, which is why an edit opened with
  // Ru filled from the row and uz/en blank even though the response carried
  // all three translations.
  const { reset } = form;
  useFormSeed(
    true,
    payload.mode === 'edit' ? String(payload.promotion.id) : undefined,
    activeBranchId === editedBranchId ? dataUpdatedAt : undefined,
    () => reset(buildDefaults(payload, detail)),
  );

  /**
   * Fold a newly-read branch's stored products into the selection.
   *
   * Only for a branch the merchant has NOT touched yet — `mergeBranchSelection`
   * leaves an existing entry alone, so a read landing after they started
   * ticking cannot undo their work.
   */
  //
  // The branch is the one the read was REQUESTED for, not `detail.restaurantId`
  // — that comes back `''` (see the seed gate above), so keying on it meant the
  // fold bailed every time and an edit opened with no products ticked at all.
  const { setValue, getValues } = form;
  const detailProductIds = detail?.productIds;
  useEffect(() => {
    if (!activeBranchId || !detailProductIds) return;
    const current = getValues('selections') ?? {};
    const next = mergeBranchSelection(current, activeBranchId, detailProductIds);
    if (next !== current) setValue('selections', next);
  }, [activeBranchId, detailProductIds, getValues, setValue]);

  const discountType = form.watch('discountType');
  const isPercent = discountType === 'percent';
  const startDate = form.watch('startDate');
  const selections = form.watch('selections') ?? {};
  // `selections` is a RECORD, so RHF types its error as a merge of the root
  // error and a per-key array — only the root carries the refinement's message,
  // and it is narrowed to a string here rather than rendered as a node.
  const selectionsError =
    typeof form.formState.errors.selections?.message === 'string'
      ? form.formState.errors.selections.message
      : undefined;

  /**
   * Floor for the promo window: midnight today, so "today" itself stays
   * pickable while every earlier day greys out.
   *
   * An EDIT floors at the start it was SAVED with instead, when that is
   * earlier. A promo that has been running since last month is a legitimate
   * record — flooring it at today would grey out its own start date and make
   * the row unsaveable without silently moving it.
   */
  const earliestStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const saved = detail ? parseIsoDate(detail.startsAt) : undefined;
    return saved && saved < today ? saved : today;
  }, [detail]);

  /**
   * Advance only if THIS step's fields validate.
   *
   * Step 2's rule is checked BY HAND. "At least one branch carries a product"
   * is a schema-level `.refine`, and `form.trigger` runs field-level validation
   * only — so triggering `selections` reported success on an empty map and let
   * the merchant walk straight past the step that defines the campaign. The
   * refine still guards the final submit; this is what surfaces it in place.
   */
  async function handleNext(): Promise<void> {
    if (step === 1) {
      if (selectedBranchIds(selections).length === 0) {
        form.setError('selections', {
          type: 'manual',
          message: t('promotions.form.productRequired'),
        });
        return;
      }
      form.clearErrors('selections');
    } else {
      const fields = STEP_FIELDS[step];
      if (fields && !(await form.trigger(fields))) return;
    }
    setStep((current) => Math.min(current + 1, STEP_FIELDS.length - 1));
  }

  function handleSubmit(values: PromotionFormValues): void {
    const ceiling = fixedPriceCeiling(values, payload, collectProductsByBranch());
    if (ceiling !== undefined) {
      form.setError('discountValue', {
        type: 'manual',
        message: t('promotions.form.fixedPriceTooHigh'),
      });
      return;
    }
    const duration = formatDuration(values.startDate, values.endDate);

    // `detail` names the products the campaign currently covers at the branch
    // it was read for — the diff against the form's selection is what tells an
    // unchecked product from one that was never on it.
    upsert.mutate(
      toUpsertVars(
        values,
        payload,
        duration,
        detail,
        collectProductsByBranch(),
        activeBranchId,
      ),
      {
        onSuccess: (outcome) => {
          // A gated merchant's write is QUEUED for moderation rather than
          // applied — the table won't show it until a moderator approves it, so
          // the queued case says so instead of closing silently.
          toast.success(
            outcome.status === 'pending'
              ? t('promotions.form.pendingReview')
              : isEditing
                ? t('promotions.form.successEdit')
                : t('promotions.form.success'),
          );
          close();
        },
        // Surface the server's own reason — a rejected promotion write is
        // usually a specific rule (an overlapping campaign, a discount the
        // branch refuses), and a generic failure hides which.
        onError: (error) => {
          const { reason } = getApiFieldErrors(error);
          toast.error(getApiErrorMessage(error, reason ?? t('promotions.form.error')));
        },
      },
    );
  }

  const title = t(isEditing ? 'common:modal.edition' : 'common:modal.creation');
  const isLastStep = step === STEP_FIELDS.length - 1;

  return (
    <Modal
      title={
        // The design's header is an orange rounded tile with a box glyph beside
        // the title, not a bare heading.
        <span className="flex items-center gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-xs">
            <MenuBoxIcon className="size-6" aria-hidden />
          </span>
          <span className="text-[1.375rem] leading-none font-semibold tracking-tight text-foreground">
            {title}
          </span>
        </span>
      }
      description={<span className="sr-only">{title}</span>}
      /* The Figma frame is 781×750 on a muted ground with a hairline under the
       * header. Capped to the viewport and pinned to `minmax(0,1fr)` so the
       * body scrolls instead of the dialog growing past the screen — the base
       * `DialogContent` is a grid whose rows size to content. */
      className={
        'grid max-h-[calc(100dvh-4rem)] w-[48.8rem] max-w-[calc(100vw-2rem)] ' +
        'grid-rows-[auto_minmax(0,1fr)] gap-0 rounded-3xl bg-muted p-5 sm:max-w-[48.8rem] ' +
        '[&>[data-slot=dialog-header]]:border-b [&>[data-slot=dialog-header]]:border-border ' +
        '[&>[data-slot=dialog-header]]:pr-12 [&>[data-slot=dialog-header]]:pb-5'
      }
      dismissible={!upsert.isPending}
    >
      {/* `min-h-0` AND `min-w-0`: a grid child defaults to `min-*: auto` on
       * BOTH axes and refuses to shrink below its content, which would defeat
       * the cap above.
       *
       * The width half is what let step 2 spill out of the dialog. The row
       * template pins the block axis (`minmax(0,1fr)`), but nothing pinned the
       * inline axis — so the two-pane step's intrinsic width became the Form's
       * floor, the Form grew wider than the dialog, and the panes (and the
       * stepper's rule, which shares this ancestor) painted straight past the
       * card's rounded right edge. */}
      <Form
        form={form}
        onSubmit={handleSubmit}
        className="flex min-h-0 min-w-0 flex-col gap-6 pt-5"
      >
        <WizardStepper
          className="shrink-0"
          steps={[
            t('promotions.form.steps.name'),
            t('promotions.form.steps.branch'),
            t('promotions.form.steps.discount'),
          ]}
          current={step}
          // Editing an existing promotion means every step already holds a
          // valid record, so all three are reachable at once — the merchant who
          // opened the wizard to change a date should not have to walk the
          // whole form. A CREATE still unlocks step by step.
          allStepsReachable={isEditing}
          onStepSelect={setStep}
        />

        {/* Every step stays MOUNTED and is hidden rather than unmounted:
         * unmounting drops the inputs' registration, so RHF discards those
         * values and a merchant returning to step 1 would find it blank.
         * `-mx-1 px-1` gives focus rings room to render inside the scroller,
         * which clips both axes once one of them scrolls. */}
        <div className="-mx-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-1">
          <div className={step === 0 ? 'flex flex-col gap-5' : 'hidden'}>
            {/* Ru and Uz side by side, En on its own line — the design's
             * two-column grid with an odd third field. */}
            <div className="grid grid-cols-2 gap-5">
              {LOCALES.map(({ suffix, tag }) => (
                <FormField key={suffix} name={`name${suffix}`} className={GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('promotions.form.nameLabel')}
                    {/* Only Ru is required — a promotion may genuinely not be
                     * translated yet, and the backend reads a blank as "not
                     * provided" rather than storing an empty string. */}
                    {suffix === 'Ru' && <span className="text-primary">*</span>}
                    <span className="font-normal text-muted-foreground">{tag}</span>
                  </FormField.Label>
                  <FormField.Input
                    className={FIELD}
                    maxLength={PROMOTION_NAME_MAX_LENGTH}
                    placeholder={t('promotions.form.namePlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>
              ))}
            </div>
          </div>

          {/* Step 2 gets a DEFINITE height, not just `flex-1`.
           *
           * `flex-1` resolves against the parent's content box, and this
           * parent is the body's `overflow-y-auto` scroller — unbounded in the
           * block direction. So the flex basis had nothing to divide, both
           * panes took their full natural height, and a merchant with a dozen
           * branches got a step that grew the dialog until it ran past its own
           * rounded edge (the modal only stops at `max-h` on a SHORT viewport,
           * which is why this looked fine in a small window and broke on a
           * tall screen).
           *
           * `h-[26rem]` is the Figma frame's own step height; `max-h-[50dvh]`
           * keeps it from dominating a short viewport, where the dialog's cap
           * is doing the bounding instead. Steps 1 and 3 stay content-sized —
           * they are a few fields and have nothing to scroll. */}
          <div
            className={
              step === 1
                ? 'flex h-[32rem] max-h-[60dvh] min-h-0 min-w-0 flex-col gap-2 sm:h-[26rem] sm:max-h-[50dvh]'
                : 'hidden'
            }
          >
            <BranchProductStep
              branches={payload.branches}
              products={branchProducts ?? []}
              value={selections}
              onChange={(next) =>
                form.setValue('selections', next, {
                  shouldValidate: form.formState.isSubmitted,
                  shouldDirty: true,
                })
              }
              activeBranchId={activeBranchId}
              onSelectBranch={setActiveBranchId}
              // Two reads gate this pane: the branch's stocked products (both
              // modes) and, on an EDIT, which of them the campaign covers.
              isLoading={
                branchProductsPending ||
                (isEditing && activeBranchId !== undefined && detailPending)
              }
            />
            {/* The «at least one product» rule reports on `selections`, which
             * has no `FormField` of its own — the picker is not a single
             * control — so the message is rendered here by hand. */}
            {selectionsError !== undefined && (
              <p className="px-1 text-sm text-destructive">{selectionsError}</p>
            )}
          </div>

          <div className={step === 2 ? 'flex flex-col gap-5' : 'hidden'}>
            <div className="grid grid-cols-2 gap-5">
              <FormField name="discountType" className={GROUP}>
                <FormField.Label className={LABEL}>
                  {t('promotions.form.discountTypeLabel')}
                </FormField.Label>
                <FormField.Select className={FIELD} options={discountTypeOptions} />
                <FormField.Message className={MESSAGE} />
              </FormField>

              <FormField name="discountValue" className={GROUP}>
                <FormField.Label className={LABEL}>
                  {isPercent
                    ? t('promotions.form.percentLabel')
                    : t('promotions.form.fixedPriceLabel')}
                </FormField.Label>
                <FormField.Price
                  className={FIELD}
                  {...(isPercent ? { maxValue: 100 } : {})}
                  placeholder={
                    isPercent
                      ? t('promotions.form.percentPlaceholder')
                      : t('promotions.form.pricePlaceholder')
                  }
                  suffix={
                    isPercent
                      ? t('promotions.form.percentSuffix')
                      : t('promotions.form.currencySuffix')
                  }
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
            </div>

            {/* Every promotion is dated — both ends are required. */}
            <div className="grid grid-cols-2 gap-5">
              <FormField name="startDate" className={GROUP}>
                <FormField.Label className={LABEL}>
                  {t('promotions.form.startLabel')}
                </FormField.Label>
                {/* A promo window cannot open in the past. Greying those days
                 * out states the rule where it applies, rather than accepting
                 * the pick and rejecting it on submit. An EDIT keeps whatever
                 * start it was already saved with — clamping to today would
                 * silently rewrite a running promo's history. */}
                <FormField.DatePicker
                  hideIcon
                  formatDate={formatDateLabel}
                  className={FIELD}
                  placeholder={t('promotions.form.datePlaceholder')}
                  disabledDates={{ before: earliestStart }}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>

              <FormField name="endDate" className={GROUP}>
                <FormField.Label className={LABEL}>
                  {t('promotions.form.endLabel')}
                </FormField.Label>
                {/* The end can never precede the start, so the floor moves with
                 * it — the schema still refuses the pair, but the calendar
                 * makes an impossible range unpickable. */}
                <FormField.DatePicker
                  hideIcon
                  formatDate={formatDateLabel}
                  className={FIELD}
                  placeholder={t('promotions.form.datePlaceholder')}
                  disabledDates={{ before: startDate ?? earliestStart }}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
            </div>
          </div>
        </div>

        <Form.RootError />

        {/* Footer: «Назад» once past the first step, then «Продолжить» or
         * «Сохранить» — right-aligned, matching every frame. */}
        <div className="flex shrink-0 items-center justify-end gap-3 pt-2">
          {step > 0 && (
            <Button
              type="button"
              onClick={() => setStep((current) => current - 1)}
              className="h-12 w-[11.125rem] rounded-full border-transparent bg-border text-base font-semibold text-foreground shadow-none hover:bg-border/70"
            >
              {t('common:actions.back')}
            </Button>
          )}

          {isLastStep ? (
            /* The two footer buttons MUST carry distinct keys — they sit in the
             * same JSX slot and differ only by props, so React would otherwise
             * reconcile them as one element and merely flip `type` on the LIVE
             * node, submitting the form on the click that reached this step. */
            <Button
              key="submit"
              type="submit"
              size="lg"
              className="h-12 w-[11.125rem] rounded-full text-base"
              disabled={upsert.isPending}
            >
              {t('common:actions.save')}
            </Button>
          ) : (
            <Button
              key="continue"
              type="button"
              size="lg"
              className="h-12 w-[11.125rem] rounded-full text-base"
              onClick={() => void handleNext()}
            >
              {t('common:actions.next')}
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
}
